<template>
  <div class="registration-support">
    <h1>{{ $t("registration_support.title") }}</h1>
    <form class="search-bar" @submit.prevent="analyze">
      <label for="support-phone">{{ $t("registration_support.phone") }}
        <input id="support-phone" v-model="phone" class="form-control" type="tel" maxlength="255" autocomplete="off" :disabled="loading" />
      </label>
      <label for="support-code">{{ $t("registration_support.credential") }}
        <input id="support-code" v-model="credential" class="form-control" maxlength="255" autocomplete="off" :disabled="loading" />
      </label>
      <label for="support-ai-requests">{{ $t("registration_support.ai_request_count") }}
        <select id="support-ai-requests" v-model.number="aiLatestRequests" class="form-control" :disabled="loading || !useAi">
          <option v-for="value in [5, 10, 15]" :key="value" :value="value">{{ $t("registration_support.latest_requests", { count: value }) }}</option>
        </select>
      </label>
      <label class="ai-toggle"><input v-model="useAi" type="checkbox" :disabled="loading" /> {{ $t("registration_support.use_ai") }}</label>
      <mdb-btn type="submit" color="indigo" :disabled="!canSubmit">
        <mdb-icon :icon="loading ? 'spinner' : 'search'" :spin="loading" class="mr-2" />
        {{ $t(loading ? "registration_support.loading" : "registration_support.analyze") }}
      </mdb-btn>
    </form>
    <p v-if="error" class="alert alert-danger" role="alert">{{ $t("registration_support.errors." + error) }}</p>
    <div aria-live="polite" :aria-busy="loading">
      <template v-if="report">
        <p class="result-meta">{{ $t("registration_support.counts", { requests: report.request_count, events: report.event_count }) }}
          <span>{{ formatTime(report.since) }} – {{ formatTime(report.until) }} (Europe/Helsinki)</span>
        </p>
        <p v-if="report.truncated" class="alert alert-warning" role="status">{{ $t("registration_support.truncated") }}</p>
        <p v-if="!report.request_count" class="alert alert-info">{{ $t("registration_support.empty") }}</p>
        <p v-else-if="report.ai.status !== 'ready'" class="alert alert-info">{{ $t("registration_support.ai_states." + report.ai.status) }}</p>
        <section v-if="analysis" class="interpretation" aria-labelledby="analysis-title">
          <h2 id="analysis-title">{{ $t("registration_support.interpretation") }}</h2>
          <p class="summary">{{ analysis.summary.text }}</p>
          <div class="evidence-links">
            <a v-for="id in analysis.summary.evidence_ids" :key="id" :href="'#support-event-' + id" @click.prevent="openEvidence(id)">{{ $t("registration_support.evidence") }} #{{ id }}</a>
          </div>
          <h3 v-if="analysis.conclusions.length">{{ $t("registration_support.conclusions") }}</h3>
          <ul><li v-for="(claim, index) in analysis.conclusions" :key="index">{{ claim.text }}
            <a v-for="id in claim.evidence_ids" :key="id" class="evidence-link" :href="'#support-event-' + id" @click.prevent="openEvidence(id)">#{{ id }}</a>
          </li></ul>
          <h3 v-if="analysis.uncertainties.length">{{ $t("registration_support.uncertainties") }}</h3>
          <ul><li v-for="(item, index) in analysis.uncertainties" :key="index">{{ item }}</li></ul>
          <h3 v-if="analysis.recommended_actions.length">{{ $t("registration_support.actions") }}</h3>
          <ul><li v-for="(item, index) in analysis.recommended_actions" :key="index">{{ item }}</li></ul>
          <p class="text-muted small">{{ $t("registration_support.ai_caveat") }}</p>
        </section>
        <section v-if="report.request_count" class="timeline" aria-labelledby="timeline-title">
          <h2 id="timeline-title">{{ $t("registration_support.timeline") }}</h2>
          <details v-for="item in displayedRequests" :key="item.request_id" :id="'support-request-' + item.request_id" class="request-entry">
            <summary>
              <time :datetime="item.created_at">{{ formatTime(item.created_at) }}</time>
              <span class="request-result" :class="outcomeClass(item)">{{ resultLabel(item) }}</span>
              <span class="request-description">{{ observation(item) }}</span>
              <span v-if="item.match === 'related'" class="text-muted small">{{ $t("registration_support.related") }}</span>
            </summary>
            <dl class="request-meta">
              <dt>{{ $t("registration_support.request") }}</dt><dd>{{ item.request_id }}</dd>
              <dt>API</dt><dd>{{ item.method }} {{ item.route }}</dd>
              <dt>{{ $t("registration_support.customer") }}</dt><dd>{{ item.customer_id || '—' }}</dd>
              <dt>{{ $t("registration_support.credential") }}</dt><dd>{{ item.credential_input || '—' }}</dd>
              <dt>HTTP / status</dt><dd>{{ item.http_status }} / {{ item.subscription_status }}</dd>
              <dt>{{ $t("registration_support.response") }}</dt><dd>{{ item.response_error || '—' }}</dd>
            </dl>
            <p v-if="!item.complete" class="alert alert-warning">{{ $t("registration_support.incomplete") }}</p>
            <details v-for="event in item.events" :key="event.id" :id="'support-event-' + event.id" class="event-entry" :class="{ highlighted: selectedEvidence === event.id }" tabindex="-1">
              <summary><strong>#{{ event.id }}</strong> <span>{{ event.stage }}</span> <code>{{ event.reason_code }}</code></summary>
              <dl class="request-meta">
                <dt>{{ $t("registration_support.customer") }}</dt><dd>{{ event.customer_id || '—' }}</dd>
                <dt>{{ $t("registration_support.submitted") }}</dt><dd>{{ event.submitted_customer_id || '—' }}</dd>
                <dt>{{ $t("registration_support.owner") }}</dt><dd>{{ event.related_customer_id || '—' }}</dd>
                <dt>{{ $t("registration_support.source") }}</dt><dd>{{ event.source || '—' }} #{{ event.source_record_id || '—' }}</dd>
                <dt>{{ $t("registration_support.credential") }}</dt><dd>{{ event.credential_input || '—' }}</dd>
                <dt>{{ $t("registration_support.matched") }}</dt><dd>{{ event.credential_matched || '—' }}</dd>
                <dt>{{ $t("registration_support.phone") }}</dt><dd>{{ event.phone_normalized || event.phone_input || event.phone_header || '—' }}</dd>
                <dt>{{ $t("registration_support.response") }}</dt><dd>{{ event.response_error || '—' }}</dd>
              </dl>
              <pre>{{ JSON.stringify(event.details, null, 2) }}</pre>
            </details>
          </details>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import { mdbBtn, mdbIcon } from "mdbvue";

export default {
  name: "RegistrationProblems",
  components: { mdbBtn, mdbIcon },
  data: () => ({ phone: "", credential: "", useAi: true, aiLatestRequests: 10, loading: false, report: null, error: "", selectedEvidence: null }),
  computed: {
    canSubmit() { return !this.loading && Boolean(this.phone.trim() || this.credential.trim()); },
    analysis() { return this.report && this.report.ai.analysis; },
    displayedRequests() { return this.report ? this.report.requests.slice().reverse() : []; },
  },
  methods: {
    async analyze() {
      if (!this.canSubmit) return;
      this.loading = true;
      this.report = null;
      this.error = "";
      this.selectedEvidence = null;
      const until = new Date();
      const since = new Date(until.getTime() - 7 * 86400000);
      try {
        const response = await this.$http.post("/1.0/front/customer-access/analyze", {
          phone: this.phone.trim(), credential: this.credential, since: since.toISOString(), until: until.toISOString(),
          use_ai: this.useAi, ai_latest_requests: this.aiLatestRequests,
        }, { timeout: 60000 });
        this.report = response.data.analyses[0];
      } catch (error) {
        const status = error.response && error.response.status;
        if (status === 401 || status === 403) {
          this.$root.$emit("session_failed");
          this.error = "unauthorized";
        } else {
          const code = error.response && error.response.data && error.response.data.error;
          this.error = ["identifier_required", "invalid_phone", "invalid_period", "invalid_search", "busy", "search_unavailable"].includes(code) ? code : "search_unavailable";
        }
      } finally {
        this.loading = false;
      }
    },
    formatTime(value) {
      return new Intl.DateTimeFormat(this.$i18n.locale === "fi" ? "fi-FI" : "en-GB", {
        timeZone: "Europe/Helsinki", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
      }).format(new Date(value));
    },
    outcomeClass(item) {
      if (!item.complete) return "incomplete";
      return item.outcome === "SUCCESS" ? "success" : item.outcome === "ERROR" ? "error" : "rejected";
    },
    resultLabel(item) { return this.$t("registration_support.results." + this.outcomeClass(item)); },
    observation(item) {
      const reasons = item.reason_codes;
      if (reasons.includes("CREDENTIAL_ASSIGNED_TO_OTHER_CUSTOMER")) return this.$t("registration_support.observations.other_owner");
      if (reasons.includes("CREDENTIAL_EXPIRED")) return this.$t("registration_support.observations.expired");
      if (item.subscription_status === 1) return this.$t("registration_support.observations.active");
      if (reasons.includes("DATABASE_ERROR") || reasons.includes("UNEXPECTED_EXCEPTION")) return this.$t("registration_support.observations.technical");
      if (reasons.includes("NO_ACTIVE_MEMBERSHIP")) return this.$t("registration_support.observations.no_membership");
      if (reasons.includes("UNIONMEMBER_FOUND")) return this.$t("registration_support.observations.union_found");
      return item.response_error || (item.method + " " + item.route);
    },
    openEvidence(id) {
      const request = this.report.requests.find(item => item.events.some(event => event.id === id));
      if (!request) return;
      this.selectedEvidence = id;
      this.$nextTick(() => {
        const requestElement = this.$el.querySelector("#support-request-" + request.request_id);
        const eventElement = this.$el.querySelector("#support-event-" + id);
        if (requestElement && eventElement) {
          requestElement.open = true;
          eventElement.open = true;
          eventElement.focus();
          eventElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
  },
};
</script>

<style scoped>
.registration-support { max-width: 1280px; margin: 0 auto; padding: 24px; color: #252a30; }
h1 { font-size: 24px; margin-bottom: 24px; }
h2 { font-size: 20px; margin: 0 0 16px; }
h3 { font-size: 16px; font-weight: 600; margin-top: 20px; }
.search-bar { display: grid; grid-template-columns: minmax(160px, 1fr) minmax(180px, 1fr) 180px; gap: 16px; align-items: end; padding-bottom: 24px; border-bottom: 1px solid #d5d9dd; }
.search-bar label { margin: 0; font-size: 14px; }
.search-bar input, .search-bar select { margin-top: 6px; }
.search-bar .ai-toggle { grid-column: 1 / 3; align-self: center; }
.ai-toggle input[type="checkbox"] { position: static; opacity: 1; pointer-events: auto; margin-right: 8px; }
.search-bar .btn { margin: 0; min-height: 42px; }
.result-meta { margin: 20px 0; font-weight: 600; }
.result-meta span { display: block; font-size: 13px; font-weight: 400; margin-top: 4px; }
.interpretation { padding: 20px 0 24px; border-bottom: 1px solid #d5d9dd; }
.summary { font-size: 17px; font-weight: 600; line-height: 1.6; }
.interpretation li { margin-bottom: 10px; }
.evidence-links { display: flex; gap: 12px; flex-wrap: wrap; }
.evidence-link { margin-left: 8px; }
.timeline { padding-top: 24px; }
.request-entry { border-top: 1px solid #d5d9dd; padding: 14px 0; }
.request-entry > summary { cursor: pointer; display: flex; gap: 12px; align-items: baseline; flex-wrap: wrap; }
.request-entry > summary::before { content: "+"; font-weight: bold; width: 12px; flex-shrink: 0; }
.request-entry[open] > summary::before { content: "−"; }
.request-entry time { font-size: 13px; font-variant-numeric: tabular-nums; }
.request-description { flex: 1 1 240px; }
.request-result { font-size: 13px; font-weight: 600; min-width: 75px; }
.success { color: #17643a; }.rejected { color: #805000; }.error { color: #ae2634; }.incomplete { color: #64666c; }
.request-meta { display: grid; grid-template-columns: 150px minmax(0, 1fr); gap: 6px 14px; margin: 20px 0; font-size: 13px; }
.request-meta dd { margin: 0; }
.event-entry { margin: 4px 0 4px 24px; padding: 10px; background: #f5f6f7; border-left: 3px solid #c7cdd3; }
.event-entry summary { cursor: pointer; font-size: 13px; }
.event-entry summary span { margin: 0 10px; }
.event-entry.highlighted { border-left-color: #2369ac; background: #edf5fc; }
pre { white-space: pre-wrap; font-size: 12px; margin: 0; }
.registration-support, pre, code, dd { overflow-wrap: anywhere; }
@media (max-width: 700px) {
  .registration-support { padding: 16px 8px; }
  .search-bar { grid-template-columns: minmax(0, 1fr); }
  .search-bar .ai-toggle { grid-column: auto; }
  .request-meta { grid-template-columns: minmax(0, 1fr); }
  .request-meta dd { margin-bottom: 8px; }
  .event-entry { margin-left: 0; }
}
</style>